import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { organizationOptionsQuery } from "@/api/queries";
import { SelectField } from "@/components/ui/field";
import { useApiClient } from "@/lib/api-context";

/**
 * Tenant narrowing for a super admin, who sees every organization at once.
 *
 * Rendered only for them, so the extra request never happens for a tenant
 * user, whose list the API scopes from the session regardless of what this
 * control would say.
 *
 * `allLabel` exists because the same picker is also used on a create form,
 * where the empty option means "you have not chosen yet" rather than "all of
 * them" — a super admin has no organization of their own to fall back to.
 */
export function OrganizationFilter({
  value,
  onChange,
  label,
  allLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  allLabel?: string;
}) {
  const { t } = useTranslation("common");
  const client = useApiClient();
  const organizations = useQuery(organizationOptionsQuery(client));

  return (
    <SelectField
      label={label ?? t("organization")}
      value={value}
      onChange={onChange}
      data={[
        { value: "", label: allLabel ?? t("allOrganizations") },
        ...(organizations.data?.data ?? []).map((organization) => ({
          value: organization.id,
          label: organization.name,
        })),
      ]}
    />
  );
}
